import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import { Component, Fragment, JSX } from "react";
import { AccordionContainer, Accordion } from "@app/shared";
import { TemplateSpecialtyGroup } from "@app/templates";
import { Waffles, wafflesKeyColumnName } from "../../shared/waffles";
import { QESCheckbox } from "@app/shared/qes-standards";
import { difference } from "lodash";

interface SpecialtyGroupsProps {
  canEdit: boolean;
  defaultOpen?: boolean;
  excludedSpecialtyGroupCodes: string[];
  disagreedExcludedSpecialtyGroupCodes?: string[];
  disagreedTelehealthSpecialtyGroupCodes?: string[];
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  handleTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCodes: string[]) => void;
  telehealthSpecialtyGroupCodes: string[];
  specialtyGroups: TemplateSpecialtyGroup[];
}

interface SpecialtyGroupsState {
  panelTitle: string;
  sortedRecords: TemplateSpecialtyGroup[];
}

interface TableData {
  code: string;
  codeDisplay: JSX.Element;
  key: string;
  name: string;
  nameDisplay: JSX.Element;
  telehealthCheckbox: JSX.Element;
}

export default class SpecialtyGroupsTable extends Component<SpecialtyGroupsProps, SpecialtyGroupsState> {
  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
  private readonly panelTitle: string = "Included Specialty Groups";

  constructor(props: SpecialtyGroupsProps) {
    super(props);
    this.state = {
      panelTitle: this.panelTitle,
      sortedRecords: props.specialtyGroups && sortBy(props.specialtyGroups, this.sortOrder) || props.specialtyGroups,
    };
  }

  public async componentDidUpdate(prevProps: SpecialtyGroupsProps) {
    if (!isEqual(prevProps.specialtyGroups, this.props.specialtyGroups)) {
      this.setState({
        panelTitle: `${this.panelTitle} (new)`,
        sortedRecords: this.props.specialtyGroups && sortBy(this.props.specialtyGroups, this.sortOrder) || this.props.specialtyGroups,
      });
    }
  }

  public render() {
    if (!this.state.sortedRecords) {
      return null;
    }

    const records: TableData[] = this.state.sortedRecords.map((rec) => {
      const telehealthCheckbox = (rec.hasTelehealthAccessPercentageCredits
        ? (
          <QESCheckbox
            label=""
            testId={`specialty-groups-table__telehealth--${rec.code}`}
            checked={this.props.telehealthSpecialtyGroupCodes?.includes(rec.code) && !this.props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
            disabled={!this.props.canEdit}
            onChange={() => this.handleTelehealthSpecialtyGroupChange(rec.code)}
            isBorderRed={this.props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
            indeterminate={this.props.disagreedTelehealthSpecialtyGroupCodes?.includes(rec.code)}
          />
        )
        : <></>);

      return {
        code: rec.code,
        codeDisplay: (<div className={this.props.disagreedExcludedSpecialtyGroupCodes?.includes(rec.code) ? "text-danger" : ""}>{rec.code}</div>),
        name: rec.name,
        nameDisplay: (<div className={this.props.disagreedExcludedSpecialtyGroupCodes?.includes(rec.code) ? "text-danger" : ""}>{rec.name}</div>),
        telehealthCheckbox,
        [wafflesKeyColumnName]: rec.code,
      };
    });

    const rowsWithTelehealth = this.state.sortedRecords.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits);
    const columnConfig = {
      codeDisplay: {
        displayText: "Code",
        testId: "code",
      },
      nameDisplay: {
        displayText: "Description",
        testId: "description",
      },
      telehealthCheckbox: {
        displayElement: (
          <span>
            <QESCheckbox
              label="Telehealth"
              className="waffles-head-checkbox"
              checked={this.props.telehealthSpecialtyGroupCodes?.length === rowsWithTelehealth.length}
              disabled={!this.props.canEdit}
              onChange={this.handleBulkTelehealthChange}
              onClick={(e) => e.stopPropagation()}
              indeterminate={this.props.telehealthSpecialtyGroupCodes && this.props.telehealthSpecialtyGroupCodes.length > 0 && this.props.telehealthSpecialtyGroupCodes.length < rowsWithTelehealth.length}
              testId="specialty-groups-table__bulk-telehealth"
            />
          </span>
        ),
        displayText: "Telehealth",
        hidden: this.state.sortedRecords.every((sr) => !sr.hasTelehealthAccessPercentageCredits),
        noSort: true,
        testId: "telehealth",
      },
    };

    return (
      <div className="form-group">
        <div>
          <AccordionContainer>
            <Accordion
              title={this.state.panelTitle}
              defaultOpen={!!this.props.defaultOpen}
              testId={`specialty-groups-table__${this.panelTitle}-accordion`}
            >
              <Fragment>
                {(this.props.canEdit || null) &&
                  <div className="mb15">
                    Check the specialty groups to include...
                  </div>
                }
                <Waffles<TableData>
                  columns={columnConfig}
                  data={records}
                  handleCheckboxChange={this.handleWaffleSelectionChange}
                  selectedKeys={records.filter((r) => !this.props.excludedSpecialtyGroupCodes?.includes(r.code)).map((m) => m.key)}
                  showCheckboxes={this.props.canEdit}
                  testId="specialty-groups-table"
                  indeterminateKeys={records.filter((r) =>
                    !!this.props.disagreedExcludedSpecialtyGroupCodes &&
                    this.props.disagreedExcludedSpecialtyGroupCodes?.includes(r.code)
                  ).map((m) => m.key)}
                />
              </Fragment>
            </Accordion>
          </AccordionContainer>
        </div>
      </div>
    );
  }

  private handleWaffleSelectionChange = (selectedCodes: string[]) => {
    const selectedProps = this.state.sortedRecords.filter((sr) => {
      return selectedCodes.includes(sr.code);
    });

    const unchecked = difference(this.props.specialtyGroups.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits), selectedProps.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits))?.map(sgc => sgc.code);
    const newTelehealth = this.props?.telehealthSpecialtyGroupCodes.filter(t => !unchecked.includes(t)) || [];
    this.props.handleTelehealthSpecialtyGroupsChange(newTelehealth);

    this.props.handleSpecialtyGroupsChange(selectedProps);
  }

  private handleBulkTelehealthChange = () => {
    const rowsWithTelehealth = this.state.sortedRecords.filter((sr) => !!sr.hasTelehealthAccessPercentageCredits);
    if (this.props.telehealthSpecialtyGroupCodes.length === rowsWithTelehealth.length) {
      this.props.handleTelehealthSpecialtyGroupsChange([]);
    } else {
      this.props.handleTelehealthSpecialtyGroupsChange(rowsWithTelehealth.map((r) => r.code));
      const currentSelectedSpecialtyGroupCodes = this.props.specialtyGroups.filter(s => !this.props.excludedSpecialtyGroupCodes.includes(s.code));
      const newSelectedSpecialtyGroupCodes = difference(rowsWithTelehealth, currentSelectedSpecialtyGroupCodes).filter(s => !this.props.telehealthSpecialtyGroupCodes.includes(s.code));
      this.props.handleSpecialtyGroupsChange(currentSelectedSpecialtyGroupCodes.concat(newSelectedSpecialtyGroupCodes));
    }
  }

  private handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCode: string) => {
    const selectedCodes = this.props.telehealthSpecialtyGroupCodes.includes(selectedSpecialtyGroupCode)
      ? this.props.telehealthSpecialtyGroupCodes.filter((sgc) => sgc !== selectedSpecialtyGroupCode)
      : this.props.telehealthSpecialtyGroupCodes.concat([selectedSpecialtyGroupCode]);

    const newExcludedSpecialtyGroupCodes = this.props.excludedSpecialtyGroupCodes.filter(e => selectedSpecialtyGroupCode !== e);
    const newSelectedSpecialtyGroupCode = this.props.specialtyGroups.filter(s => !newExcludedSpecialtyGroupCodes.includes(s.code));
    this.props.handleSpecialtyGroupsChange(newSelectedSpecialtyGroupCode);

    this.props.handleTelehealthSpecialtyGroupsChange(selectedCodes);
  }
}
